import { FC } from 'react'

import styled, { css } from 'styled-components'

import PaperclipIcon from 'src/Assets/Svg/paperclip.svg?react'
import CloseIcon from 'src/Assets/Svg/close-cross.svg?react'
import { variables } from 'src/Styled/variables'

import { formatFileSize } from './validation'

export type AudioFileUploadTrackItemProps = {
  name: string
  size?: number
  progress?: number
  uploadedBytes?: number
  totalBytes?: number
  secondsLeft?: number | null
  processing?: boolean
  uploaded?: boolean
  onRemove?: () => void
}

const MIN_PROGRESS = 0
const MAX_PROGRESS = 100
const SECONDS_IN_MINUTE = 60

const normalizeProgress = (progress: number): number =>
  Number.isFinite(progress)
    ? Math.round(Math.min(MAX_PROGRESS, Math.max(MIN_PROGRESS, progress)))
    : MIN_PROGRESS

export const formatTimeLeft = (secondsLeft: number): string =>
  secondsLeft < SECONDS_IN_MINUTE
    ? 'less than a minute left'
    : `about ${Math.ceil(secondsLeft / SECONDS_IN_MINUTE)} min left`

const textStyles = css`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: ${variables.lineHeight150per};
`

const Container = styled.div<{ $uploaded: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 8px 8px 16px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};

  ${({ $uploaded }) =>
    $uploaded &&
    css`
      border-color: ${({ theme }) => theme.color.brandGreen};
    `}
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
`

const IconWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  color: ${({ theme }) => theme.color.darkGrey};
`

const Name = styled.div`
  ${textStyles};
  flex: 1 0 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Details = styled.div`
  ${textStyles};
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  text-align: right;
  white-space: nowrap;
`

const RemoveButton = styled.button`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 40px;
  background: transparent;
  color: ${({ theme }) => theme.color.darkGrey};
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.color.hoverWhite};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.brandGreen};
    outline-offset: 2px;
  }
`

const Progress = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-right: 8px;
  padding-bottom: 8px;
`

const ProgressTrack = styled.div`
  width: 100%;
  height: 4px;
  overflow: hidden;
  border-radius: 4px;
  background: ${({ theme }) => theme.color.lightGrey};
`

const ProgressFill = styled.div.attrs<{ $percentage: number }>(
  ({ $percentage }) => ({ style: { width: `${$percentage}%` } }),
)<{ $percentage: number }>`
  height: 100%;
  border-radius: 4px;
  background: ${({ theme }) => theme.color.brandGreen};
  transition: width 0.2s;
`

const ProgressText = styled.div`
  ${textStyles};
`

const getProgressText = ({
  percentage,
  uploadedBytes,
  totalBytes,
  secondsLeft,
  processing,
}: {
  percentage: number
  uploadedBytes?: number
  totalBytes?: number
  secondsLeft?: number | null
  processing?: boolean
}): string => {
  if (processing) {
    return 'Processing…'
  }
  if (typeof uploadedBytes !== 'number' || typeof totalBytes !== 'number') {
    return `${percentage}%`
  }
  const sizeText = `${formatFileSize(uploadedBytes)} of ${formatFileSize(totalBytes)}`
  return typeof secondsLeft === 'number' && secondsLeft > 0
    ? `${sizeText} · ${formatTimeLeft(secondsLeft)}`
    : sizeText
}

export const AudioFileUploadTrackItem: FC<AudioFileUploadTrackItemProps> = ({
  name,
  size,
  progress,
  uploadedBytes,
  totalBytes,
  secondsLeft,
  processing,
  uploaded,
  onRemove,
}) => {
  const showProgress = !uploaded && typeof progress === 'number'
  const percentage = processing
    ? MAX_PROGRESS
    : normalizeProgress(progress ?? MIN_PROGRESS)

  return (
    <Container
      $uploaded={Boolean(uploaded)}
      aria-label={`${name}, ${uploaded ? 'uploaded' : 'uploading'}`}
    >
      <Row>
        <IconWrapper>
          <PaperclipIcon width={16} height={16} />
        </IconWrapper>
        <Name title={name}>{name}</Name>
        {typeof size === 'number' && (
          <Details>
            <span>{formatFileSize(size)}</span>
          </Details>
        )}
        <RemoveButton
          type='button'
          aria-label={`Remove ${name}`}
          onClick={onRemove}
        >
          <CloseIcon width={16} height={16} />
        </RemoveButton>
      </Row>
      {showProgress && (
        <Progress>
          <ProgressTrack
            role='progressbar'
            aria-label={`Uploading ${name}`}
            aria-valuemin={MIN_PROGRESS}
            aria-valuemax={MAX_PROGRESS}
            aria-valuenow={percentage}
          >
            <ProgressFill $percentage={percentage} />
          </ProgressTrack>
          <ProgressText aria-live='polite'>
            {getProgressText({
              percentage,
              uploadedBytes,
              totalBytes,
              secondsLeft,
              processing,
            })}
          </ProgressText>
        </Progress>
      )}
    </Container>
  )
}
